<template>
  <div class="card listing-card">
    <div class="card-body">
      <div class="form-group row">
        <label for="properties" class="col-lg-4 label-text">
          <span class="text-danger">*</span>Associated Properties
        </label>
        <div class="loading col-lg-4" v-if="propertiesLoading || servicePropertiesLoading">
          Loading...
        </div>
        <div class="col-lg-8">
          <template v-if="parentNamespace === 'Instrument'">
            <b-form-checkbox-group
              v-if="!propertiesLoading"
              v-model="associatedPropertyIds"
              v-validate="'multi_required'"
              data-vv-as="Associated Property"
              name="properties"
            >
              <b-form-checkbox
                v-for="property in orderedProjectProperties"
                :key="property.id"
                :value="property.id"
                class="col-md-5 noselect mb-2">
                  {{ getPropertyLabel(property)}}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </template>
          <template v-else>
            <b-form-checkbox-group
              v-if="!servicePropertiesLoading"
              v-model="associatedPropertyIds"
              v-validate="'multi_required'"
              data-vv-as="Associated Property"
              name="properties"
            >
              <b-form-checkbox
                v-for="property in serviceProperties"
                :key="property.id"
                :value="property.id"
                class="col-md-5 noselect mb-2">
                  {{ getPropertyLabel(property)}}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </template>
          <div v-if="errors.first('properties')" class="invalid-feedback d-block mb-2">
            {{ errors.first('properties') }}
          </div>
        </div>
      </div>
      <div class="form-group row">
        <label for="additionalProperties" class="col-lg-4 label-text">
          Cross-Project Properties
        </label>
        <div class="col-lg-8">
          <ul v-if="associatedProjects.length !== 0" class="list-group mb-2">
            <li class="list-group-item" v-for="project in associatedProjects" :key="project.id">
              <div class="d-flex justify-content-between">
                <h6>Project {{project.id}} - Current Properties</h6>
                <a class="form-action-text text-secondary p-0"
                   @click='checkAllForProject(project)'>
                  <font-awesome-icon icon='check' />
                    Select All Properties
                </a>
                <a class="form-action-icon text-danger p-0"
                   @click='removeProject(project)'>
                  <font-awesome-icon icon='trash-alt' />
                </a>
              </div>
              <b-form-checkbox-group
                v-model="crossProjectPropertyIds"
                :key="project.id"
                :name="`properties-${project.id}`"
              >
                <b-form-checkbox
                  v-for="property in project.properties"
                  :key="property.id"
                  :value="property.id"
                  class="col-md-5 noselect mb-2"
                >
                  {{ getPropertyLabel(property)}}
                </b-form-checkbox>
              </b-form-checkbox-group>
            </li>
          </ul>
          <div class="row">
            <GetProperties
              class="col-md-10"
              :serviceId="currentService.id"
              :projects="allProjects"
              @projectAdded="addProject"
            />
          </div>
        </div>
      </div>
    </div>
    <div class='card-footer right-align-content'>
      <b-button
        :disabled="submitting"
        variant="danger"
        class="button-row-spacing"
        v-b-modal="'cancelManagingAssociatedProperties'"
      >
        Cancel
      </b-button>
      <b-button
        :disabled="submitting"
        @click="submitWrapper()">
        Save
      </b-button>
      <DeleteModal
        modalId="removeAssociatedProperty"
        deleteDisplayName="Remove Associated Property"
        :deletePrompt="warningMessage"
        @deleteConfirmed="submitConfirmed()">
      </DeleteModal>
      <CancelModal
          modalId="cancelManagingAssociatedProperties"
          ref='cancelModal'
          @cancelled="cancel">
      </CancelModal>
      <b-modal ref="confirmationModal" :id="'confirmationBoxModal'" title='Update Box Folder'>
        {{ confirmationMessage }}
        <div slot="modal-footer">
          <b-button @click="hideConfirmationModal">
            Okay
          </b-button>
        </div>
      </b-modal>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import api from '@/api';
import DeleteModal from '../DeleteModal.vue';
import FormSubmit from '@/mixins/FormSubmit';
import CancelModal from '@/components/CancelModal.vue';
import GetProperties from '@/components/GetProperties.vue';
import { EntityModules } from '@/store/moduleConfig';

export default {
  name: 'ManageAssociatedProperties',
  mixins: [FormSubmit],
  props: {
    storeNamespace: String,
    parentNamespace: {
      type: String,
      default: 'Instrument',
    },
  },
  data() {
    return {
      serviceProperties: [],
      servicePropertiesLoading: false,
      associatedPropertyIds: [],
      associatedProjects: [],
      crossProjectPropertyIds: [],
      warningMessage: 'All Impacted Acres & Development Rights values will be removed. Are you sure you want to remove this Property?',
      initialPropertyIds: [],
    };
  },
  mounted() {
    this.$store.dispatch('Common/startEditMode', this.parentNamespace);
    this.$store.dispatch('Property/fetchProperties', parseInt(this.$route.params.project_id, 10));
    if (this.parentNamespace === 'Service') {
      this.getServiceProperties();
    }
    this.setPropertyIdValues();
    this.setAssociatedProperties();
  },
  components: {
    CancelModal,
    GetProperties,
    DeleteModal,
  },
  computed: {
    ...mapState('Instrument', {
      submittingInstrument: state => state.submitting,
      currentInstrument: state => state.currentInstrument,
    }),
    ...mapState('Service', {
      submittingService: state => state.submitting,
      currentService: state => state.currentService,
    }),
    ...mapState('Property', {
      propertiesLoading: state => state.propertiesLoading,
    }),
    ...mapGetters('Property', {
      orderedProjectProperties: 'getOrderedCurrentProperties',
    }),
    submitting() {
      return this.submittingInstrument || this.submittingService;
    },
    allProjects() {
      return this.associatedProjects.concat({ id: parseInt(this.$route.params.project_id, 10) });
    },
    confirmationMessage() {
      return `Box folder for Service ${this.$route.params.id} may need to be moved as appropriate.`;
    },
  },
  methods: {
    getPropertyLabel(property) {
      if (property.calculatedAcres) {
        return `${property.id} - ${this.$options.filters.acresFormat(property.calculatedAcres)} Acres`;
      }
      return `${property.id} - Unspecified Acreage`;
    },
    setPropertyIdValues() {
      this.associatedPropertyIds = this.$store.state[this.storeNamespace].entities
        .filter(prop => prop.projectId === parseInt(this.$route.params.project_id, 10))
        .map(prop => prop.id);
      this.crossProjectPropertyIds = this.$store.state[this.storeNamespace].entities
        .filter(prop => prop.projectId !== parseInt(this.$route.params.project_id, 10))
        .map(prop => prop.id);
      this.initialPropertyIds = this.associatedPropertyIds.concat(this.crossProjectPropertyIds);
    },
    setAssociatedProperties() {
      const projectIds = [...new Set(this.$store.state[this.storeNamespace].entities
        .map(ap => ap.projectId))]
        .filter(ap => ap !== parseInt(this.$route.params.project_id, 10));
      this.associatedProjects = [];

      if (this.parentNamespace === 'Instrument') {
        projectIds.forEach((pi) => {
          api.get(`/project/${pi}/currentproperties`)
            .then((response) => {
              this.associatedProjects.push({ id: pi, properties: response.data });
            });
        });
      } else {
        const serviceId = this.currentService.id;
        projectIds.forEach((pi) => {
          api.get(`/project/${pi}/getvalidpropertiesbyservice/${serviceId}`)
            .then((response) => {
              this.associatedProjects.push({ id: pi, properties: response.data });
            });
        });
      }
    },
    getServiceProperties() {
      this.servicePropertiesLoading = true;
      const projectId = parseInt(this.$route.params.project_id, 10);
      const serviceId = this.currentService.id;

      api.get(`/project/${projectId}/getvalidpropertiesbyservice/${serviceId}`)
        .then((response) => {
          this.serviceProperties = response.data;
          this.servicePropertiesLoading = false;
        });
    },
    addProject(project) {
      this.associatedProjects.push(project);
    },
    removeProject(project) {
      this.crossProjectPropertyIds = this.crossProjectPropertyIds
        .filter(propertyId => project.properties && !project.properties.some(prop => prop.id === propertyId));
      this.associatedProjects.splice(
        this.associatedProjects.findIndex(ap => ap.id === project.id),
        1,
      );
    },
    cancel() {
      this.stopEdit();
    },
    stopEdit() {
      this.$emit('closeForm');
      this.$store.dispatch('Common/stopEditMode', this.parentNamespace);
    },
    cleanUpAfterSubmit() {
      if (this.parentNamespace === 'Service') {
        this.showConfirmationModal();
      } else {
        this.finishCleanAfterSubmit();
      }
    },
    checkAllForProject(project) {
      const uniqueIds = this.crossProjectPropertyIds;
      uniqueIds.push(...project.properties.map(property => property.id));
      this.crossProjectPropertyIds = [...new Set(uniqueIds)];
    },
    isParentInstrument() {
      return this.parentNamespace === 'Instrument';
    },
    parentId() {
      return this.isParentInstrument() ? this.currentInstrument.id : this.currentService.id;
    },
    submitWrapper() {
      if (this.isParentInstrument() && this.isPropertyIdRemoved()) {
        this.$bvModal.show('removeAssociatedProperty');
      } else {
        this.submitConfirmed();
      }
    },
    submitConfirmed() {
      const entity = {};
      if (this.isParentInstrument()) {
        Object.assign(entity, this.currentInstrument);
        entity.isAssociatedPropertyUpdate = true;
      } else {
        Object.assign(entity, this.currentService);
      }

      entity.associatedPropertyIds = this.associatedPropertyIds
        .concat(this.crossProjectPropertyIds);
      this.onSubmit(entity, this.parentNamespace, null, this.cleanUpAfterSubmit);
    },
    isPropertyIdRemoved() {
      const newList = this.associatedPropertyIds.concat(this.crossProjectPropertyIds);
      return !this.initialPropertyIds
        .every(propertyId => newList.includes(propertyId));
    },
    showConfirmationModal() {
      this.$refs.confirmationModal.show();
    },
    hideConfirmationModal() {
      this.$refs.confirmationModal.hide();
      this.finishCleanAfterSubmit();
    },
    finishCleanAfterSubmit() {
      this.stopEdit();
      // const test = this.proje;

      this.$store.dispatch('Common/stopEditMode', this.parentNamespace);

      let shouldRefreshPage = true;
      const allSelectedPropertyIds = this.associatedPropertyIds
        .concat(this.crossProjectPropertyIds);

      this.orderedProjectProperties.forEach((x) => {
        if (allSelectedPropertyIds.includes(x.id)) {
          shouldRefreshPage = false;
        }
      });

      if (this.parentNamespace === 'Service' && shouldRefreshPage) {
        this.$store.dispatch('Service/forceFetchServices', parseInt(this.$route.params.project_id, 10))
          .then(() => {
            this.$router.push({
              name: 'projectServices',
              params: { project_id: parseInt(this.$route.params.project_id, 10) },
            });
          });
      } else {
        this.$store.dispatch(`${this.storeNamespace}/fetchEntities`, {
          parentType: this.parentNamespace,
          parentId: this.parentId(),
          entityType: EntityModules.ASSOCIATED_PROPERTIES.entityType,
          forceLoad: true,
        }).then(() => {
          this.setPropertyIdValues();
          this.setAssociatedProperties();
        });
      }
    },
  },
};
</script>
